import brandingStore from "stores/brandingStore";
import { SurveyFormFace } from "helpers/enum";
import { cloneDeep } from "lodash";
import { SCOPES, TRIPETTO_SECTIONS, isHttpUrl, resolveSetValue } from "./brandingSchema";
import { SURVEY_RUNNER_DEFAULTS } from "./tripettoRunnerDefaults";

export const TripettoTarget = { Survey: "survey", Form: "form" };

// Behavior is not configurable in Branding; these keep the runner defaults used before branding
// (see vendor/roi-tripetto/runner/styles.ts), since some runners default differently.
const BASE_FLAGS = {
  [SurveyFormFace.Autoscroll]: {
    autoFocus: false, showEnumerators: false, hideRequiredIndicator: false, showProgressbar: false,
    showNavigation: "auto", showPreviousButton: true, showSeparateSubmit: true,
    direction: "vertical", verticalAlignment: "middle",
    hidePast: false, hideUpcoming: false, showScrollbar: false,
  },
  [SurveyFormFace.Classic]: {
    mode: "paginated", autoFocus: false, showEnumerators: false, hideRequiredIndicator: false,
    showProgressbar: false, showPageIndicators: false,
  },
  [SurveyFormFace.Chat]: { autoFocus: false, showScrollbar: false, display: "inline", fullWidth: false, autoOpen: false },
};

const setPath = (object, path, value) => {
  const parts = path.split(".");
  let cursor = object;
  parts.slice(0, -1).forEach(part => {
    cursor[part] = cursor[part] || {};
    cursor = cursor[part];
  });
  cursor[parts[parts.length - 1]] = value;
};

/**
 * Styles for one survey/form layout (a SurveyFormFace) from the Surveys & Forms branding.
 * Only settings the layout supports, and only values the company set (or derived from them), are applied.
 * Runners don't deep-merge styles, so surveys start from ROI's runner defaults for the face; forms start empty.
 */
const buildTripettoStyles = (layers, face, target) => {
  const base = target === TripettoTarget.Survey ? cloneDeep(SURVEY_RUNNER_DEFAULTS[face] || {}) : {};
  const styles = { ...base, noBranding: true, ...BASE_FLAGS[face] };
  TRIPETTO_SECTIONS.forEach(section => {
    section.fields.forEach(f => {
      if (!f.runners.includes(face)) return;
      const value = resolveSetValue(layers, SCOPES.SURVEYS_FORMS, f.key);
      if (value === undefined || value === "") return;
      if (f.type === "text" && !isHttpUrl(value)) return; // the text fields are image URLs
      setPath(styles, f.path, value);
    });
  });
  if (styles.avatar && styles.avatar.image) styles.avatar.type = "image";
  return styles;
};

/** Branded styles for a new survey/form, or null when the company has not saved branding. */
export const getBrandedTripettoStyles = (face, target = TripettoTarget.Survey) =>
  brandingStore.saved ? buildTripettoStyles(brandingStore.saved, face, target) : null;

/**
 * When the layout changes, swap in the new layout's branded styles, but only if the current
 * styles are still the untouched branded ones. Hand-edited styles are never overwritten.
 */
export const restyleOnFaceChange = (currentStyles, oldFace, newFace, target = TripettoTarget.Survey) => {
  const untouched = getBrandedTripettoStyles(oldFace, target);
  if (untouched && JSON.stringify(untouched) === JSON.stringify(currentStyles)) {
    return getBrandedTripettoStyles(newFace, target);
  }
  return currentStyles;
};
